// Ask Hopscotch chat engine — the streaming/threading logic behind the
// companion panel, lifted from the old RightPanel (HOP-10 thread semantics
// preserved verbatim) plus the inline action-button protocol.
//
// Correctness crux, unchanged: the thread KEY is captured at stream start
// and every callback writes through store actions to that captured key, so
// navigating mid-stream keeps tokens flowing into the thread they belong
// to, never the newly active one.

import { useState, useRef, useEffect, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useGlobalState } from '@/stores/globalState'
import { apiClient } from '@/lib/apiClient'
import { capture } from '@/lib/analytics'
import { useCompanionContext, useCompanionThreadKey } from '@/lib/chatContext'
import { streamChat, type ChatAction, type ChatWireMessage } from '@/lib/chatApi'
import { dispatchCompanionAction, drainQueuedAddGames } from '@/lib/companionActions'
import type { ChatMessage, ChatMessageAction } from '@/types'

const EMPTY_MESSAGES: ChatMessage[] = []

interface WireThreadMessage {
  id: string
  role: string
  content: string
  action?: { id: string; name: string; label: string; params: Record<string, unknown> } | null
  createdAt: string
}

export interface CompanionChat {
  threadKey: string
  messages: ChatMessage[]
  isStreaming: boolean
  error: string | null
  send: (text: string) => void
  retry: () => void
  clear: () => void
  runMessageAction: (msg: ChatMessage) => void
}

export function useCompanionChat(): CompanionChat {
  const chatContext = useCompanionContext()
  const threadKey = useCompanionThreadKey()
  const activeLearnerId = useGlobalState((s) => s.activeLearnerId)
  const isGenerating = useGlobalState((s) => s.isGenerating)

  const thread = useGlobalState((s) => s.chatThreads[threadKey])
  const messages = thread?.messages ?? EMPTY_MESSAGES
  const isStreaming = thread?.streaming ?? false

  // Error banner scoped to the thread it happened in — switching threads
  // hides it naturally; switching back shows it again.
  const [threadError, setThreadError] = useState<{ key: string; message: string } | null>(null)
  const error = threadError && threadError.key === threadKey ? threadError.message : null

  // One in-flight chat stream per thread.
  const abortControllersRef = useRef(new Map<string, AbortController>())
  const prevLearnerRef = useRef(activeLearnerId)

  // Learner change: abort in-flight streams. Threads themselves are NOT
  // wiped — the active key changes naturally.
  useEffect(() => {
    if (prevLearnerRef.current !== activeLearnerId) {
      for (const [key, controller] of abortControllersRef.current) {
        controller.abort()
        useGlobalState.getState().setThreadStreaming(key, false)
      }
      abortControllersRef.current.clear()
      prevLearnerRef.current = activeLearnerId
    }
  }, [activeLearnerId])

  // When a generation finishes, dispatch the next queued add_game action.
  const prevIsGeneratingRef = useRef(isGenerating)
  useEffect(() => {
    const wasGenerating = prevIsGeneratingRef.current
    prevIsGeneratingRef.current = isGenerating
    if (wasGenerating && !isGenerating) drainQueuedAddGames()
  }, [isGenerating])

  // Hydrate a guide thread from the backend's saved session the first time
  // it's opened this app session (and after reloads).
  const needsHydration =
    !!chatContext.guideId && !thread?.hydrated && !thread?.messages.length
  const { data: savedThread } = useQuery({
    queryKey: ['chat-thread', chatContext.guideId],
    queryFn: async () => {
      const res = await apiClient.get(`/api/chat/threads?guideId=${chatContext.guideId}`)
      return res.data as { thread: { sessionId: string; messages: WireThreadMessage[] } | null }
    },
    enabled: needsHydration,
    staleTime: Infinity,
  })
  useEffect(() => {
    if (!needsHydration || savedThread === undefined || !chatContext.guideId) return
    const restored: ChatMessage[] = (savedThread.thread?.messages ?? [])
      // Keep messages that carry text OR a restorable action button; drop
      // the truly empty rows (auto-dispatch actions persist no button).
      .filter((m) => m.content || m.action)
      .map((m) => ({
        id: m.id,
        role: m.role.toLowerCase() === 'user' ? ('user' as const) : ('assistant' as const),
        content: m.content,
        timestamp: new Date(m.createdAt),
        ...(m.action
          ? {
              action: {
                id: m.action.id,
                name: m.action.name,
                label: m.action.label,
                params: m.action.params ?? {},
                status: 'idle' as const,
              },
            }
          : {}),
      }))
    useGlobalState.getState().hydrateThread(chatContext.guideId, {
      messages: restored,
      sessionId: savedThread.thread?.sessionId ?? null,
    })
  }, [needsHydration, savedThread, chatContext.guideId])

  // Shared streaming starter for send + retry.
  const startStream = useCallback(
    (key: string, history: ChatWireMessage[], assistantId: string) => {
      let actionSeen = false
      let receivedText = false

      const store = useGlobalState.getState()
      store.setThreadStreaming(key, true)
      const currentSessionId = store.chatThreads[key]?.sessionId ?? null

      const controller = streamChat(history, chatContext, currentSessionId, {
        onSessionId: (id) => useGlobalState.getState().setThreadSession(key, id),
        onChunk: (chunk) => {
          receivedText = true
          useGlobalState.getState().appendToThreadMessage(key, assistantId, chunk)
        },
        onAction: (action: ChatAction) => {
          // One action per response — a model retry can't double-build.
          if (actionSeen) return
          actionSeen = true
          // Old backends only ever sent add_game and expected immediate
          // dispatch, hence the name fallback.
          const auto = action.autoDispatch ?? action.name === 'add_game'
          if (auto) {
            dispatchCompanionAction({ name: action.name, params: action.params }, key)
          } else {
            const button: ChatMessageAction = {
              id: action.id ?? `act-${Date.now()}`,
              name: action.name,
              label: action.label ?? action.name,
              params: action.params,
              status: 'idle',
            }
            useGlobalState.getState().patchThreadMessage(key, assistantId, { action: button })
          }
        },
        onDone: () => {
          // Tool call with no accompanying text → fallback copy.
          if (actionSeen && !receivedText) {
            useGlobalState.getState().patchThreadMessage(key, assistantId, {
              content: 'Here you go:',
            })
          }
          useGlobalState.getState().setThreadStreaming(key, false)
          abortControllersRef.current.delete(key)
        },
        onError: (message) => {
          setThreadError({ key, message })
          useGlobalState.getState().setThreadStreaming(key, false)
          abortControllersRef.current.delete(key)
        },
      })
      abortControllersRef.current.set(key, controller)
    },
    [chatContext],
  )

  const send = useCallback(
    (text: string) => {
      capture('companion_message_sent', { thread: threadKey })
      setThreadError(null)
      const store = useGlobalState.getState()
      const current = store.chatThreads[threadKey]?.messages ?? []

      const userMsg: ChatMessage = {
        id: `msg-${Date.now()}`,
        role: 'user',
        content: text,
        timestamp: new Date(),
      }
      const assistantId = `msg-${Date.now()}-ai`
      const assistantMsg: ChatMessage = {
        id: assistantId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
      }

      const history: ChatWireMessage[] = [
        ...current
          .filter((m) => (m.role === 'user' || m.role === 'assistant') && m.content)
          .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content })),
        { role: 'user' as const, content: text },
      ]

      store.appendThreadMessage(threadKey, userMsg)
      store.appendThreadMessage(threadKey, assistantMsg)
      startStream(threadKey, history, assistantId)
    },
    [threadKey, startStream],
  )

  const retry = useCallback(() => {
    const store = useGlobalState.getState()
    const current = store.chatThreads[threadKey]?.messages ?? []
    const lastUserMsg = [...current].reverse().find((m) => m.role === 'user')
    if (!lastUserMsg) return

    const last = current[current.length - 1]
    if (last?.role === 'assistant') {
      store.removeThreadMessage(threadKey, last.id)
    }
    setThreadError(null)
    const assistantId = `msg-${Date.now()}-retry`
    const assistantMsg: ChatMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
    }

    const trimmed = useGlobalState.getState().chatThreads[threadKey]?.messages ?? []
    const history: ChatWireMessage[] = trimmed
      .filter((m) => (m.role === 'user' || m.role === 'assistant') && m.content)
      .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content }))

    store.appendThreadMessage(threadKey, assistantMsg)
    startStream(threadKey, history, assistantId)
  }, [threadKey, startStream])

  const clear = useCallback(() => {
    const controller = abortControllersRef.current.get(threadKey)
    if (controller) {
      controller.abort()
      abortControllersRef.current.delete(threadKey)
    }
    useGlobalState.getState().resetThread(threadKey)
    setThreadError(null)
  }, [threadKey])

  // Inline action button clicked: dispatch once, disable the button.
  const runMessageAction = useCallback(
    (msg: ChatMessage) => {
      if (!msg.action || msg.action.status === 'done') return
      useGlobalState.getState().patchThreadMessage(threadKey, msg.id, {
        action: { ...msg.action, status: 'done' },
      })
      dispatchCompanionAction({ name: msg.action.name, params: msg.action.params }, threadKey)
    },
    [threadKey],
  )

  return { threadKey, messages, isStreaming, error, send, retry, clear, runMessageAction }
}
